import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import type {
  CreateOrderSetBody,
  OrderSetDetail,
  OrderSetStatus,
  OrderSetSummary,
  OrderSetWriteResult,
  UpdateOrderSetBody,
} from '@/types/order-set-api';

/**
 * Order sets data layer (PROD-2710, release 1: personal sets). Contract: `@/types/order-set-api`.
 *
 * Every call acts on the CALLER's own sets (there is no provider id on the wire), so the
 * cache keys carry no user axis: the query client is per-session.
 */
export const ORDER_SETS_BASE = '/api/Patients/OrderSets';

export function orderSetsQueryKey(status: OrderSetStatus) {
  return ['settings', 'order-sets', status] as const;
}

export function orderSetQueryKey(id: string | null | undefined) {
  return ['settings', 'order-set', id] as const;
}

/** GET /OrderSets?status=active|archived. The API has no search; the tab filters client-side. */
export function useOrderSets(status: OrderSetStatus, enabled = true) {
  return useQuery({
    queryKey: orderSetsQueryKey(status),
    queryFn: ({ signal }) =>
      get<OrderSetSummary[]>(`${ORDER_SETS_BASE}?status=${status}`, { signal }),
    enabled,
  });
}

/** GET /OrderSets/{id}. Disabled until an id is known. */
export function useOrderSet(id: string | null | undefined) {
  return useQuery({
    queryKey: orderSetQueryKey(id),
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useOrderSet called without an id');
      return get<OrderSetDetail>(`${ORDER_SETS_BASE}/${encodeURIComponent(id)}`, { signal });
    },
    enabled: !!id,
    // An edit starts from `version`; a background refetch mid-edit must not swap the draft's base.
    staleTime: Infinity,
  });
}

/** Both list slots plus the one set's detail. */
function invalidateOrderSets(queryClient: QueryClient, id: string) {
  void queryClient.invalidateQueries({ queryKey: ['settings', 'order-sets'] });
  void queryClient.invalidateQueries({ queryKey: orderSetQueryKey(id) });
}

/** POST /OrderSets → 201 `{ id, version }`. The API needs a name and at least one item. */
export function useCreateOrderSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrderSetBody) => post<OrderSetWriteResult>(ORDER_SETS_BASE, body),
    onSuccess: (res) => invalidateOrderSets(queryClient, res?.id ?? ''),
  });
}

/** PUT /OrderSets/{id}. A stale `expectedVersion` answers 409 `{ error }` (an ApiError). */
export function useUpdateOrderSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrderSetBody }) =>
      put<OrderSetWriteResult>(`${ORDER_SETS_BASE}/${encodeURIComponent(id)}`, body),
    onSuccess: (_res, { id }) => invalidateOrderSets(queryClient, id),
  });
}

/** POST /OrderSets/{id}/archive → 204. */
export function useArchiveOrderSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post<void>(`${ORDER_SETS_BASE}/${encodeURIComponent(id)}/archive`, undefined),
    onSuccess: (_res, id) => invalidateOrderSets(queryClient, id),
  });
}

/** POST /OrderSets/{id}/restore → 204. */
export function useRestoreOrderSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post<void>(`${ORDER_SETS_BASE}/${encodeURIComponent(id)}/restore`, undefined),
    onSuccess: (_res, id) => invalidateOrderSets(queryClient, id),
  });
}
